import React, { useState } from 'react';
import { Menu, X, Layers } from 'lucide-react';
import { colors, spacing, typography, borders, shadows } from '../../tokens';
import { ConnectButton } from '../../molecules/web3/ConnectButton';

interface SidebarProps {
  onNavigate?: (page: string) => void;
  currentPage?: string;
  showConnectButton?: boolean;
}

export const Sidebar: React.FC<SidebarProps> = ({
  onNavigate,
  currentPage = 'stakingpage',
  showConnectButton = true,
}) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [hoveredLink, setHoveredLink] = useState<string | null>(null);

  const navLinks = [
    { id: 'stakingpage', label: 'STAKING', href: '/staking', icon: Layers },
  ];

  const handleLinkClick = (linkId: string) => {
    if (onNavigate) {
      onNavigate(linkId);
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      {/* Mobile Menu Button */}
      <button
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        style={{
          position: 'fixed',
          top: spacing.md,
          left: spacing.md,
          zIndex: 2000,
          display: 'none',
          padding: spacing.sm,
          backgroundColor: colors.neutral.white,
          border: `1px solid ${colors.border.default.default}`,
          borderRadius: borders.component.button.radius,
          cursor: 'pointer',
          boxShadow: shadows.md,
        }}
        className="mobile-menu-button"
      >
        {isMobileMenuOpen ? <X size={24} color={colors.text.primary.default} /> : <Menu size={24} color={colors.text.primary.default} />}
      </button>

      {/* Sidebar */}
      <aside
        style={{
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          width: '220px',
          backgroundColor: colors.neutral.white,
          borderRight: `1px solid ${colors.border.default.default}`,
          display: 'flex',
          flexDirection: 'column',
          zIndex: 1500,
          transform: isMobileMenuOpen ? 'translateX(0)' : undefined,
          transition: 'transform 0.3s ease',
        }}
        className="sidebar"
      >
        {/* Logo */}
        <div
          style={{
            padding: `${spacing.xl} ${spacing.lg}`,
            borderBottom: `1px solid ${colors.border.default.default}`,
          }}
        >
          <img
            src="/farlogo.svg"
            alt="FARLABS"
            style={{
              height: '32px',
              width: 'auto',
            }}
          />
        </div>

        {/* Navigation Links */}
        <nav
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            padding: `${spacing.lg} 0`,
            overflowY: 'auto',
          }}
        >
          {navLinks.map((link) => {
            const isActive = currentPage === link.id;
            const isHovered = hoveredLink === link.id;

            return (
              <div
                key={link.id}
                onClick={() => handleLinkClick(link.id)}
                onMouseEnter={() => setHoveredLink(link.id)}
                onMouseLeave={() => setHoveredLink(null)}
                style={{
                  position: 'relative',
                  padding: `${spacing.md} ${spacing.lg}`,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  transition: 'all 0.2s ease',
                  backgroundColor: isActive
                    ? `${colors.brand.primary.base}10`
                    : isHovered
                    ? colors.background.secondary.default
                    : 'transparent',
                  borderLeft: isActive
                    ? `3px solid ${colors.brand.primary.base}`
                    : '3px solid transparent',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: spacing.sm,
                  }}
                >
                  {link.icon && (
                    <link.icon
                      size={18}
                      color={isActive ? colors.brand.primary.base : colors.text.primary.default}
                      style={{ transition: 'color 0.2s ease' }}
                    />
                  )}
                  <span
                    style={{
                      fontSize: typography.fontSize.sm,
                      fontWeight: isActive ? typography.fontWeight.semibold : typography.fontWeight.medium,
                      color: isActive ? colors.brand.primary.base : colors.text.primary.default,
                      letterSpacing: '0.5px',
                      transition: 'color 0.2s ease',
                    }}
                  >
                    {link.label}
                  </span>
                </div>
              </div>
            );
          })}
        </nav>

        {/* Connect Wallet Button at Bottom */}
        {showConnectButton && (
          <div
            style={{
              padding: spacing.lg,
              borderTop: `1px solid ${colors.border.default.default}`,
            }}
          >
            <ConnectButton />
          </div>
        )}
      </aside>

      {/* Overlay for mobile */}
      {isMobileMenuOpen && (
        <div
          onClick={() => setIsMobileMenuOpen(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1400,
            display: 'none',
          }}
          className="mobile-overlay"
        />
      )}

      {/* Responsive Styles */}
      <style>{`
        @media (max-width: 768px) {
          .mobile-menu-button {
            display: block !important;
          }
          .sidebar {
            transform: translateX(-100%);
          }
          .sidebar[style*="translateX(0)"] {
            transform: translateX(0) !important;
          }
          .mobile-overlay {
            display: block !important;
          }
        }
      `}</style>
    </>
  );
};
